/* eslint-disable no-restricted-properties */

import { createEnv } from "@t3-oss/env-core"
import { z } from "zod"

// must be relative, import alias cannot be read by next.config.ts at the moment - probably because its transpiled to commonjs not esm - cant read "imports" array from package.json
import { zBooleanOnly } from "../validation/primitives"
import { createCIEnvValidationHandler } from "./envValidationHandler"

const serverExternalBackendValidation = {
  INTERNAL_API_URL: z.string({
    required_error: "Used to access the backend api inside f.e. the kubernetes cluster",
  }),
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const serverExternalBackendValidationObject = z.object(serverExternalBackendValidation)
type EnvVariablesWithoutExternalBackend = Omit<ReturnType<typeof internal_nextEnv>, keyof z.infer<typeof serverExternalBackendValidationObject>>

/**
 * @param NOT_AVAILABLE_ON_CI
 * @param hasExternalBackend defines whether a backend outside of nextjs exists, that is requested
 * for example springboot backend or other custom servers
 * If only a database is used and accessed via nextjs backend apis, set this to false
 */
function internal_nextEnv(NOT_AVAILABLE_ON_CI: string[], hasExternalBackend: boolean) {
  return createEnv({
    emptyStringAsUndefined: true,
    server: {
      ...(hasExternalBackend ? serverExternalBackendValidation : {}),
    },
    client: {
      NEXT_PUBLIC_API_URL: z.string({
        required_error: "Used to access the backend api from the outside world - e.g. browser",
      }),
      NEXT_PUBLIC_DEPLOYED: zBooleanOnly.optional(),
      NEXT_PUBLIC_APP_ENV: z.enum(["development", "staging", "production"]),
      NEXT_PUBLIC_NODE_ENV: z.enum(["development", "test", "production"]),
    },
    clientPrefix: "NEXT_PUBLIC_",
    runtimeEnv: {
      // If new env variables are added and get an ESLINT error for turbo/... add them to the root turbo.json globalEnv array and restart ESLINT service of editor if error stays

      ...(hasExternalBackend ? { INTERNAL_API_URL: process.env.INTERNAL_API_URL } : {}),
      NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
      NEXT_PUBLIC_DEPLOYED: process.env.NEXT_PUBLIC_DEPLOYED,
      NEXT_PUBLIC_APP_ENV: process.env.NEXT_PUBLIC_APP_ENV,
      NEXT_PUBLIC_NODE_ENV: process.env.NODE_ENV,
    },

    onValidationError: createCIEnvValidationHandler(NOT_AVAILABLE_ON_CI),
  })
}

export function nextEnv<TExternalBackend extends boolean>(
  NOT_AVAILABLE_ON_CI: string[],
  hasExternalBackend: TExternalBackend,
): TExternalBackend extends true ? ReturnType<typeof internal_nextEnv> : EnvVariablesWithoutExternalBackend {
  return internal_nextEnv(NOT_AVAILABLE_ON_CI, hasExternalBackend)
}
